import Panel from '@movable/studio-ar/panel';
import html2canvas from '@movable/html2canvas';
import { hideTag, showTag, tagOnHold, setupTapToChange } from '@movable/studio-ar/utils';
import FaceTracker from './face-tracker';
import RecordRTC from 'recordrtc';
import GifRecorder from './gif-recorder';

import generateColorizedShader from './shaders/colorized';
import grayscaleShader from './shaders/grayscale';
import generateDuotoneShader from './shaders/duotone';

const recordTimeLimit = 6000; // ms

const dpi = window.devicePixelRatio || 1;

const hiddenForRecording = [
  'tool-camera',
  'tool-tap-to-change',
  'background',
  'hide-for-photo',
  'face-track-image',
  'silhouette',
  'tool-record',
  'tap-for-photo',
  'detectionText'
];

export default class VideoPanel extends Panel {
  async render() {
    this.parent.replaceTokens(this.tags, this.parent.tokens || {});

    hideTag(this.getTagByTool('countdown'));
    hideTag(this.getTagByTool('frame-indicator'));

    const camera = this.getTagByTool('camera');
    if (!camera) {
      throw new Error('missing camera tag in the video panel');
    }

    const drawableTags = this.tags.filter(t => {
      return !hiddenForRecording.find(c => t.element.classList.contains(c));
    });

    const htmlTags = drawableTags.filter(t => t.type !== 'staticPic');
    const imageTags = drawableTags.filter(t => t.type === 'staticPic');

    await Promise.all(
      htmlTags.map(async tag => {
        tag.canvas = await html2canvas(tag.element, { backgroundColor: null });
      })
    );

    imageTags.forEach(t => (t.imageSrc = t.imageUrl));

    // since the tags are now baked into the frame
    drawableTags.forEach(t => hideTag(t));

    await this.setupFaceFilter(camera);

    return this.setupHandlers();
  }

  setupFaceFilter(camera) {
    const videoContainer = camera.element;

    const canvas = document.createElement('canvas');
    canvas.classList.add('face-tracker');
    canvas.classList.add('mirror');
    canvas.width = camera.width * dpi;
    canvas.height = camera.height * dpi;

    videoContainer.innerHTML = '';
    videoContainer.appendChild(canvas);

    const faceTrackImages = this.tags.filter(t => t.classNames.match('face-track-image'));
    const mouthOpenOnlyImages = this.tags.filter(t => t.classNames.match('mouth-open-only'));

    let shader;
    const colorFilter = this.parent.fields.color_filter;

    if (colorFilter === 'tint') {
      const colorTint = this.parent.fields.color_tint;
      shader = generateColorizedShader(colorTint);
    } else if (colorFilter === 'grayscale') {
      shader = grayscaleShader;
    } else if (colorFilter === 'duotone') {
      const { fields } = this.parent;
      const colorTint = fields.color_tint;
      const duotoneBackground = fields.duotone_background;
      const duotoneBrightness = fields.duotone_brightness;
      const duotoneContrast = fields.duotone_contrast;

      shader = generateDuotoneShader(
        colorTint,
        duotoneBackground,
        duotoneContrast,
        duotoneBrightness
      );
    } else if (this.parent.fragmentShader) {
      shader = this.parent.fragmentShader;
    }

    this.tracker = new FaceTracker(canvas, {
      trackedImages: faceTrackImages,
      tags: this.tags.filter(t => t.canvas || t.imageSrc),
      facingMode: this.parent.fields.camera_direction === 'face' ? 'user' : 'environment',
      maxFaces: this.parent.fields.max_faces,
      onFaceDetectionChange: this.onFaceDetectionChange.bind(this),
      onMouthDetectionChange: this.onMouthDetectionChange.bind(this),
      vertexShader: shader
    });

    this.showForPhotoImages = this.tags.filter(t => t.classNames.match('show-for-photo'));

    return this.tracker.init().then(() => {
      faceTrackImages.forEach(hideTag);
      mouthOpenOnlyImages.forEach(hideTag);
      this.showForPhotoImages.forEach(hideTag);
    });
  }

  onMouthDetectionChange(isDetected, threePlane) {
    threePlane.children.forEach(child => {
      if (child.userData.mouthOpenOnly) {
        child.visible = isDetected;
      }
      if (child.userData.mouthClosedOnly) {
        child.visible = !isDetected;
      }
    });

    this.tags
      .filter(t => t.classNames.match('mouth-open-only'))
      .filter(t => !t.classNames.match('face-track-image'))
      .forEach(t => (isDetected ? showTag(t) : hideTag(t)));

    this.tags
      .filter(t => t.classNames.match('mouth-closed-only'))
      .filter(t => !t.classNames.match('face-track-image'))
      .forEach(t => (isDetected ? hideTag(t) : showTag(t)));
  }

  onFaceDetectionChange(isDetected, face) {
    face.visible = isDetected;
    this.tags
      .filter(t => t.classNames.match('silhouette'))
      .forEach(t => t.element.classList[isDetected ? 'add' : 'remove']('hidden'));
  }

  setupHandlers() {
    setupTapToChange(
      this.tags.filter(t => t.toolName === 'tap-to-change'), // click area tag(s)
      this.tags.filter(t => t.classNames.match(/tap-to-change/)), // tags to rotate between
      'tap-to-change',
      position => this.nextTapContent(position)
    );

    const recordTag = this.getTagByTool('record');

    const spinner = document.createElement('div');
    spinner.classList.add('spinner');
    spinner.innerHTML =
      '<svg viewBox="0 0 100 100"><circle cx="50%" cy="50%" r="43px"></circle></svg><div class="inner"></div>';
    recordTag.element.appendChild(spinner);

    tagOnHold(this.getTagByTool('record'), async tapFinish => {
      hideTag(this.getTag('tooltip'));

      const recording = await this.recordVideo(tapFinish);

      this.saveRecording(recording);
    });

    if (!this.parent.fields.record_gif) {
      this.getTag('tooltip').element.classList.add('noGIF');
    }
  }

  async recordVideo(tapFinish) {
    let recorder;
    const recordButton = this.getTagByTool('record');

    this.tracker.toggleSlow(true);
    this.showForPhotoImages.forEach(showTag);
    await new Promise(resolve => setTimeout(resolve, 200));
    const initialPhoto = await this.takeSingle();
    this.tracker.toggleSlow(false);

    const recordElement = (recordButton && recordButton.element) || document.createElement();

    recordElement.classList.add('recording');

    try {
      if (window.navigator.userAgent.match(/iPhone|iPad/) || typeof MediaRecorder === 'undefined') {
        recorder = new RecordRTC.RecordRTCPromisesHandler(this.tracker.canvas, {
          recorderType: GifRecorder,
          type: 'agif', // using 'gif' produces weird behavior
          frameRate: 90 // ms per frame
        });
      } else {
        const stream = this.tracker.canvas.captureStream();
        recorder = new RecordRTC.RecordRTCPromisesHandler(stream, {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8'
        });
      }

      recorder.startRecording();
    } catch (e) {
      console.log('Recording video seems to be unsupported.');
      console.error(e);
      recordElement.classList.remove('recording');
    }

    const startTime = new Date();
    const timeout = new Promise(resolve => setTimeout(resolve, recordTimeLimit));

    await Promise.race([tapFinish, timeout]);

    recordElement.classList.remove('recording');

    try {
      const duration = new Date() - startTime;

      if (!this.parent.fields.record_gif || duration < 250) {
        recorder.stopRecording();
        return initialPhoto;
      }

      recordElement.classList.add('processing');

      await new Promise(resolve => setTimeout(resolve, 10));
      await recorder.stopRecording();

      return recorder.getBlob();
    } catch (e) {
      console.error(e);
      return initialPhoto;
    } finally {
      recordElement.classList.remove('processing');
      if (recorder) {
        recorder.destroy();
      }
    }
  }

  saveRecording(blob) {
    this.parent.sendEvent('take_picture');
    this.parent.imageDataURL = window.URL.createObjectURL(blob);
    this.parent.blobby = blob;

    this.parent.transitionToPanel('review');
  }

  nextTapContent(position) {
    this.tags
      .filter(t => t.tapToChangePosition != null)
      .filter(t => !t.classNames.match('face-track-image'))
      .forEach(t => {
        const inThisPosition = t.tapToChangePosition === position;
        if (!t.classNames.match('mouth-open-only') && inThisPosition) {
          showTag(t);
        } else {
          hideTag(t);
        }
      });

    this.tracker.faces.forEach(face => {
      this.tracker.planeForFace(face).children.forEach(child => {
        const { tapToChangePosition } = child.userData;
        child.visible = tapToChangePosition && tapToChangePosition === position;
      });
    });
  }

  takeSingle() {
    return new Promise(resolve => {
      return this.tracker.canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });
  }

  activate() {
    this.tracker && this.tracker.togglePause(false);
    URL.revokeObjectURL(this.parent.imageDataURL);
    this.parent.blobby = null;
    this.showForPhotoImages.forEach(hideTag);
  }

  deactivate() {
    this.tracker && this.tracker.togglePause(true);
    const recordButton = this.getTagByTool('record');
    recordButton && recordButton.element.classList.remove('processing');
  }
}
